import { useState, type FormEvent } from 'react';
import { KeyRound, Mail } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { safeReturnTo } from '../auth/return-to';
import { Button, Card } from '../components/ui';
import './LoginPage.css';

export function LoginPage() {
  const { status, passkeyAvailable, requestMagicLink, signInWithPasskey } = useAuth();
  const location = useLocation();
  const returnTo = safeReturnTo(location.search);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState<'passkey' | 'magic' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (status === 'authenticated') {
    return <Navigate to={returnTo} replace />;
  }

  const handlePasskey = async () => {
    setBusy('passkey');
    setError(null);
    setMessage(null);
    try {
      await signInWithPasskey(email.trim() || undefined);
    } catch {
      setError('Passkey sign-in could not be completed. Try the email link instead.');
    } finally {
      setBusy(null);
    }
  };

  const sendMagicLink = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('magic');
    setError(null);
    setMessage(null);
    try {
      await requestMagicLink(email.trim());
      setMessage('Check your email. The sign-in link expires shortly and works once.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in could not be completed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="login-page">
      <Card className="login-card">
        <div className="login-brand" aria-label="ZoloTalk">
          ZoloTalk
        </div>
        <h1>Welcome back</h1>
        <p className="login-intro">
          Sign in to translate, practice, and view your private history.
        </p>

        <form className="login-form" onSubmit={sendMagicLink}>
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email webauthn"
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
          />

          <Button
            type="button"
            size="lg"
            loading={busy === 'passkey'}
            disabled={!passkeyAvailable || busy !== null}
            onClick={() => void handlePasskey()}
          >
            <KeyRound size={20} aria-hidden="true" />
            Sign in with a passkey
          </Button>
          {!passkeyAvailable && (
            <p className="login-hint">
              Passkeys are unavailable in this browser. Use email instead.
            </p>
          )}

          <div className="login-divider" aria-hidden="true">
            <span>or</span>
          </div>

          <Button
            type="submit"
            variant="secondary"
            size="lg"
            loading={busy === 'magic'}
            disabled={!email.trim() || busy !== null}
          >
            <Mail size={20} aria-hidden="true" />
            Email me a sign-in link
          </Button>
        </form>

        {message && (
          <p className="login-message" role="status">
            {message}
          </p>
        )}
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
      </Card>
    </main>
  );
}
